import css from './index.module.scss';
import { Button, CheckboxLabel, IconNode, ToolTip, Typography } from '@components/base';
import Images from '@assets/images';
import {
  calculateSavedQuoteCMValues,
  getCommaSeperatedCurrencyAmount,
  getFormattedTime,
  sanitizeValue
} from '@helpers/utils';
import { endOfDay, format } from 'date-fns';
import ViewQuoteModal from '../view-quote';
import AddEditQuote from '../add-edit-quote';
import { useEffect } from 'react';
import { DeletePrompt } from '@components/common';
import CMValue from '@components/common/quote-wrapper/components/cm-value';

const ConsolidationDetail = (props: any) => {
  const {
    productData,
    quoteDetail,
    setQuoteDetail,
    fetchEnquiryDetail,
    showQuotes,
    setShowQuotes
  } = props;
  const {
    openViewQuoteModal,
    detailModalData,
    isConsolidation,
    openAddEditQuoteModal,
    addEditQuoteModalData,
    addEditQuoteIsEdit,
    isFcl,
    isMtt,
    selectedQuotes,
    primaryUnit,
    addEditQuoteIsNew,
    showDisclaimer,
    currency1,
    fxRate
  } = quoteDetail;
  const cm2Context = { currency1, fxRate, primaryUnit };

  useEffect(() => {
    if (productData?.length > 0) {
      const allExpanded = productData.reduce((acc: any, _item: any, index: number) => {
        acc[index] = true;
        return acc;
      }, {});
      setShowQuotes(allExpanded);
    }
  }, [productData, setShowQuotes]);

  const toggleQuotesVisibility = (productIndex: number) => {
    setShowQuotes((prev: any) => ({
      ...prev,
      [productIndex]: !prev[productIndex]
    }));
  };

  const getFormattedDate = (date: any) => {
    if (date) {
      return format(endOfDay(new Date(date).getTime()), 'do LLL');
    } else {
      return null;
    }
  };

  const closeViewQuoteModal = () => {
    setQuoteDetail((prevState: any) => {
      return {
        ...prevState,
        openViewQuoteModal: false
      };
    });
  };

  const isEnquirySelected = (quote: any) => {
    return selectedQuotes.some(
      (q: any) => q.consolidation_details_id === quote.consolidation_details_id
    );
  };

  const handleEnquiryCheck = (quote: any) => {
    setQuoteDetail((prevState: any) => {
      return {
        ...prevState,
        selectedQuotes: isEnquirySelected(quote)
          ? prevState.selectedQuotes.filter(
              (q: any) => q.consolidation_details_id !== quote.consolidation_details_id
            )
          : [...prevState.selectedQuotes, quote]
      };
    });
  };

  const handleAddQuoteClick = (item: any) => {
    const quote = item.quotes?.[0];
    setQuoteDetail((prevState: any) => {
      return {
        ...prevState,
        openAddEditQuoteModal: true,
        addEditQuoteModalData: {
          products: (quote.products || []).map((product: any) => {
            return {
              product_data: product.product_data
            };
          }),
          consolidation_id: item.consolidation_id
        },
        addEditQuoteIsEdit: false,
        addEditQuoteIsNew: true
      };
    });
  };

  return (
    <main className={css.mainWrapper}>
      {productData.map((item: any, index: number) => {
        return (
          <div key={index} className={css.productWrapper}>
            <section
              className={showQuotes[index] ? css.header : css.headerCollapsed}
              onClick={() => toggleQuotesVisibility(index)}
            >
              <div className={css.title}>
                <Typography variant="label">Products</Typography>
                <div className={css.products}>
                  {item.quotes[0].products.map((product: any, productIndex: number) => {
                    return (
                      <div className={css.productNameWrapper} key={productIndex}>
                        <Typography variant="h5">
                          {product.product_data?.name}
                          {productIndex < item.quotes[0].products.length - 1 && ', '}
                        </Typography>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className={css.buttonWrapper}>
                <Button
                  variant="contained"
                  onClick={() => {
                    handleAddQuoteClick(item);
                  }}
                >
                  Add Quote
                </Button>
                <ToolTip title={showQuotes[index] ? 'Collapse' : 'Expand'}>
                  <div>
                    <IconNode src={showQuotes[index] ? Images.arrowUpBig : Images.arrowDownBig} />
                  </div>
                </ToolTip>
              </div>
            </section>
            {showQuotes[index] &&
              item.quotes.map((quote: any, quoteIndex: number) => {
                const incoTerm = quote?.deal_incoterm;
                let incoTermLocation = incoTerm + ' - ';
                switch (incoTerm) {
                  case 'EXW':
                  case 'DDP':
                  case 'DAP':
                    incoTermLocation += quote?.deal_incoterm_location;
                    break;

                  case 'FOB':
                    incoTermLocation += quote?.pol?.name;
                    break;

                  case 'CIF':
                  case 'CNF':
                  case 'CFR':
                    incoTermLocation += quote?.pod?.name;
                    break;

                  default:
                    incoTermLocation += quote?.pod?.name;
                    break;
                }
                return (
                  <div key={quoteIndex} className={css.quoteWrapper}>
                    <section className={css.content}>
                      <div className={css.contentHeader}>
                        <div className={css.contentHeaderText}>
                          <div className={css.checkBoxKeyValue}>
                            <div className={css.checkBox}>
                              <CheckboxLabel
                                label=""
                                checked={isEnquirySelected(quote)}
                                onChange={() => handleEnquiryCheck(quote)}
                                className={css.checkboxLabel}
                              />
                              <div className={css.checkedKeyValue}>
                                <div className={css.key}>Total Weight</div>
                                <div className={css.value}>
                                  {Number(quote.total_weight ?? 0)} {quote.total_weight_unit}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className={css.keyValue}>
                            <div className={css.key}>Inco Term</div>
                            <div className={css.value}>{incoTermLocation}</div>
                          </div>
                        </div>
                        <div className={css.contentHeaderIcons}>
                          <ToolTip title="View Quote" placement="left">
                            <div>
                              <IconNode
                                src={Images.eyeRed}
                                alt="View Quote"
                                className={css.editIcon}
                                onClick={() => {
                                  setQuoteDetail((prevState: any) => {
                                    return {
                                      ...prevState,
                                      openViewQuoteModal: true,
                                      detailModalData: {
                                        ...quote
                                      }
                                    };
                                  });
                                }}
                              />
                            </div>
                          </ToolTip>
                          <ToolTip title="Duplicate Quote" placement="top">
                            <div>
                              <IconNode
                                src={Images.duplicateRed}
                                alt="Duplicate Quote icon"
                                className={css.editIcon}
                                onClick={() => {
                                  setQuoteDetail((prevState: any) => {
                                    return {
                                      ...prevState,
                                      openAddEditQuoteModal: true,
                                      addEditQuoteModalData: {
                                        ...quote,
                                        consolidation_id: item.consolidation_id
                                      },
                                      addEditQuoteIsEdit: false
                                    };
                                  });
                                }}
                              />
                            </div>
                          </ToolTip>
                          <ToolTip title="Edit Quote" placement="right">
                            <div>
                              <IconNode
                                src={Images.editRed}
                                alt="Edit Quote"
                                className={css.editIcon}
                                onClick={() => {
                                  setQuoteDetail((prevState: any) => {
                                    return {
                                      ...prevState,
                                      openAddEditQuoteModal: true,
                                      addEditQuoteModalData: {
                                        ...quote,
                                        consolidation_id: item.consolidation_id
                                      },
                                      addEditQuoteIsEdit: true
                                    };
                                  });
                                }}
                              />
                            </div>
                          </ToolTip>
                        </div>
                      </div>
                      {quote.products.map((product: any, productIndex: number) => {
                        const productCMValues = calculateSavedQuoteCMValues(
                          product,
                          cm2Context,
                          quote
                        );
                        return (
                          <div key={productIndex} className={css.contentBody}>
                            <div className={css.contentBodyItem}>
                              <div className={css.keyValue}>
                                <div className={css.key}>Product Name</div>
                                <div className={css.value}>{product?.product_data?.name}</div>
                                <div className={css.dateCaption}>
                                  {getFormattedDate(product.created_at)}{' '}
                                  {getFormattedTime(product.created_at)}
                                </div>
                              </div>
                              <div className={css.keyValue}>
                                <div className={css.key}>Quantity</div>
                                <div className={css.value}>
                                  {Number(product?.quantity ?? 0)} {product?.quantity_unit}
                                </div>
                              </div>
                              <div className={css.keyValue}>
                                <div className={css.key}>
                                  Quoted Amount{' '}
                                  <span>
                                    ( Per{' '}
                                    {product?.final_price?.unit_of_weight || primaryUnit?.label} )
                                  </span>
                                </div>
                                <div className={css.value} style={{ fontWeight: 'bold' }}>
                                  {product?.final_price
                                    ? getCommaSeperatedCurrencyAmount(
                                        product?.final_price?.currency,
                                        product?.final_price?.value
                                      )
                                    : '-'}
                                </div>
                              </div>
                              <div className={css.cmKeyValue}>
                                <div className={css.key}>Expected CM1</div>
                                <div className={css.cmValue}>
                                  <CMValue
                                    amount={productCMValues.cm1}
                                    percent={productCMValues.cm1Percent}
                                    currency={currency1?.label}
                                  />
                                </div>
                              </div>
                              <div className={css.cmKeyValue}>
                                <div className={css.key}>Expected CM2</div>
                                <div className={css.cmValue}>
                                  <CMValue
                                    amount={productCMValues.cm2}
                                    percent={productCMValues.cm2Percent}
                                    currency={currency1?.label}
                                  />
                                </div>
                              </div>
                              <div className={css.truncateKeyValue}>
                                <div className={css.key}>Product Packaging</div>
                                <div
                                  className={css.value}
                                  title={sanitizeValue(product?.packaging)}
                                >
                                  {sanitizeValue(product?.packaging)}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </section>
                  </div>
                );
              })}
          </div>
        );
      })}
      {openViewQuoteModal && (
        <ViewQuoteModal
          open={openViewQuoteModal}
          onClose={closeViewQuoteModal}
          data={detailModalData}
          cm2Context={cm2Context}
          isConsolidation={isConsolidation}
          primaryUnit={primaryUnit?.label}
        />
      )}
      {openAddEditQuoteModal && (
        <AddEditQuote
          open={openAddEditQuoteModal}
          onClose={() => {
            setQuoteDetail((prevState: any) => {
              return {
                ...prevState,
                showDisclaimer: true
              };
            });
          }}
          onSubmitClose={() => {
            setQuoteDetail((prevState: any) => {
              return {
                ...prevState,
                openAddEditQuoteModal: false,
                addEditQuoteIsEdit: false,
                addEditQuoteIsNew: false
              };
            });
          }}
          isConsolidation={isConsolidation}
          isFcl={isFcl}
          isMtt={isMtt}
          data={addEditQuoteModalData}
          isEdit={
            addEditQuoteIsNew == true ? 'add' : addEditQuoteIsEdit == true ? 'edit' : 'duplicate'
          }
          quoteDetail={quoteDetail}
          fetchEnquiryDetail={fetchEnquiryDetail}
        />
      )}
      {showDisclaimer && (
        <DeletePrompt
          open={showDisclaimer}
          onClose={() =>
            setQuoteDetail((prevState: any) => ({
              ...prevState,
              showDisclaimer: false
            }))
          }
          onConfirm={() =>
            setQuoteDetail((prevState: any) => ({
              ...prevState,
              showDisclaimer: false,
              openAddEditQuoteModal: false,
              addEditQuoteIsEdit: false,
              addEditQuoteIsNew: false
            }))
          }
          message="Are you sure you want to close?"
        />
      )}
    </main>
  );
};

export default ConsolidationDetail;
